const MIDA_READY_TIMEOUT_MS = 2000
const MIDA_POLL_INTERVAL_MS = 50

const ONBOARDING_CHECKOUT_PENDING_KEY = "mida_onboarding_checkout_pending"
// Bound how long a "pending" checkout can sit in storage. Without this, an abandoned
// free_trial checkout (tab close / navigation away) would linger forever and could let a
// later, unrelated subscription success page satisfy the backstop and mis-fire the event.
const ONBOARDING_CHECKOUT_PENDING_TTL_MS = 30 * 60 * 1000
const onboardingSuccessKey = (subscriptionId?: string) => `mida_onboarding_success_${subscriptionId || "unknown"}`

// localStorage can throw (private mode, disabled storage, etc.); never let analytics
// bookkeeping break the checkout flow.
const safeGetItem = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

const safeSetItem = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // ignore
  }
}

const safeRemoveItem = (key: string) => {
  try {
    window.localStorage.removeItem(key)
  } catch {
    // ignore
  }
}

const waitForMida = (timeoutMs = MIDA_READY_TIMEOUT_MS): Promise<boolean> =>
  new Promise(resolve => {
    if (window.mida?.isLoaded) {
      resolve(true)
      return
    }
    const start = Date.now()
    const intervalId = window.setInterval(() => {
      if (window.mida?.isLoaded) {
        window.clearInterval(intervalId)
        resolve(true)
      } else if (Date.now() - start >= timeoutMs) {
        window.clearInterval(intervalId)
        resolve(false)
      }
    }, MIDA_POLL_INTERVAL_MS)
  })

export const trackMidaABTestEvent = async (event: string) => {
  try {
    if (window.mida?.isLoaded) {
      await window.mida.track(event)
      return
    }

    // Wait for the SDK to finish loading rather than silently queuing into window.mdq,
    // which is lost if the page navigates before Mida flushes it.
    if (await waitForMida()) {
      await window.mida.track(event)
      return
    }
  } catch {
    // mida.track threw/rejected; fall through to the queue so the event isn't lost.
  }

  // Last resort: queue and hope the SDK loads before navigation.
  window.mdq = Array.isArray(window.mdq) ? window.mdq : []
  window.mdq.push(["track", event])
}

// True only when a free_trial checkout was marked pending recently. Expired markers are
// cleared so they can't mis-attribute a later, unrelated subscription success page.
const hasFreshOnboardingCheckoutPending = (): boolean => {
  const raw = safeGetItem(ONBOARDING_CHECKOUT_PENDING_KEY)
  if (!raw) return false
  const markedAt = Number(raw)
  if (!Number.isFinite(markedAt) || Date.now() - markedAt > ONBOARDING_CHECKOUT_PENDING_TTL_MS) {
    safeRemoveItem(ONBOARDING_CHECKOUT_PENDING_KEY)
    return false
  }
  return true
}

// Marks that a combined (free_trial) onboarding checkout has started. Only checkouts that
// set this flag are eligible for the success-page backstop, which keeps the
// onboardingSubscriptionSuccess event scoped to /onboarding/free_trial.
export const markOnboardingCheckoutPending = () => {
  safeSetItem(ONBOARDING_CHECKOUT_PENDING_KEY, String(Date.now()))
}

// Clears the pending flag on terminal non-success outcomes (cancel/error) so an abandoned
// free_trial checkout can't later mis-attribute a different subscription's success page.
export const clearOnboardingCheckoutPending = () => {
  safeRemoveItem(ONBOARDING_CHECKOUT_PENDING_KEY)
}

// Explicit fire from the free_trial card flow. Idempotent per subscription_id so reloads
// and the success-page backstop never double-count.
export const trackOnboardingSubscriptionSuccess = (subscriptionId?: string) => {
  const guardKey = onboardingSuccessKey(subscriptionId)
  if (safeGetItem(guardKey)) return
  safeSetItem(guardKey, "1")
  safeRemoveItem(ONBOARDING_CHECKOUT_PENDING_KEY)
  trackMidaABTestEvent("onboardingSubscriptionSuccess")
}

// Backstop fired when the server-rendered success page loads. Recovers redirect-based
// (wallet / 3DS) conversions where onCreateSubscriptionSuccess never runs. Only fires when a
// free_trial checkout is pending and the event hasn't already fired for this subscription.
export const trackOnboardingSubscriptionSuccessBackstop = (subscriptionId?: string) => {
  if (!hasFreshOnboardingCheckoutPending()) return
  const guardKey = onboardingSuccessKey(subscriptionId)
  if (safeGetItem(guardKey)) return
  safeSetItem(guardKey, "1")
  safeRemoveItem(ONBOARDING_CHECKOUT_PENDING_KEY)
  trackMidaABTestEvent("onboardingSubscriptionSuccess")
}
